import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersAPI, transactionsAPI, cardsAPI, walletAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Save, Plus, Minus, QrCode, Download, Smartphone, CreditCard } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';


// Apple icon component
const AppleIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [customer, setCustomer] = useState(null);
  const [cards, setCards] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const [pointsForm, setPointsForm] = useState({
    card_id: '',
    points: 1,
    transaction_type: 'earn',
  });


  // P2-2 — wallet installation status per card
  const [walletStatus, setWalletStatus] = useState({}); // { [card_id]: { apple_installed, google_save_link_generated } }

  const fetchWalletStatus = useCallback(async (customerId) => {
    try {
      const { data } = await customersAPI.walletStatus(customerId);
      const map = {};
      (data.cards || []).forEach((c) => {
        map[c.card_id] = c;
      });
      setWalletStatus(map);
    } catch (err) {
      // Non-fatal: badges just won't appear
      console.warn('wallet-status fetch failed', err);
    }
  }, []);

  const fetchCards = useCallback(async () => {
    try {
      const { data } = await cardsAPI.list();
      setCards(data);
    } catch (err) {
      console.error('Failed to fetch cards:', err);
    }
  }, []);

  const fetchCustomer = useCallback(async () => {
    try {
      const { data } = await customersAPI.get(id);
      setCustomer(data);
      setFormData({
        name: data.name,
        email: data.email || '',
        phone: data.phone || '',
      });
      // Auto-select the first ENROLLED card (not the first merchant card),
      // so manual point updates can't accidentally hit a card the customer
      // is not enrolled on. Backend now strict-rejects with 400 not_enrolled.
      if (data.card_balances?.length > 0) {
        setPointsForm(prev => ({ ...prev, card_id: data.card_balances[0].card_id }));
      }
    } catch (err) {
      console.error('Failed to fetch customer:', err);
      setError('Impossible de charger ce client. Rechargez la page.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCards();
    if (!isNew) {
      fetchCustomer();
      fetchWalletStatus(id);
    }
  }, [fetchCards, fetchCustomer, fetchWalletStatus, isNew, id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email && !formData.phone) {
      setError('Email ou téléphone requis');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const { data } = await customersAPI.create(formData);
        navigate(`/dashboard/customers/${data.id}`);
      } else {
        await customersAPI.update(id, formData);
        fetchCustomer();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Stamp cards speak in "tampons", points cards in "points".
  const cardById = (cid) => cards.find((c) => c.id === cid);
  const isStampCard = (card) => card?.card_type === 'stamps';
  const allStamps = cards.length > 0 && cards.every(isStampCard);
  const selectedCard = cardById(pointsForm.card_id);
  const unit = isStampCard(selectedCard) ? 'tampons' : (selectedCard ? 'points' : (allStamps ? 'tampons' : 'points'));
  const balanceLabel = (card, points) => (isStampCard(card)
    ? `${points}/${card.total_stamps || 10} tampons`
    : `${points} pts`);

  const handleAddPoints = async (type) => {
    if (!pointsForm.card_id) {
      setError('Veuillez sélectionner une carte de fidélité');
      return;
    }
    const pts = parseInt(pointsForm.points, 10);
    if (!pts || pts < 1 || pts > 100000) {
      setError(`Indiquez un nombre de ${unit} entre 1 et 100 000`);
      return;
    }
    setError('');

    try {
      const resp = await transactionsAPI.create({
        customer_id: id,
        card_id: pointsForm.card_id,
        points: pts,
        transaction_type: type,
      });
      // Reward feedback when a palier was just crossed (stamps modulo reset
      // or points threshold reset). Without this toast the merchant would not
      // see that the balance was reset and would wrongly think the tap failed.
      if (resp?.data?.reward_earned) {
        toast.success(
          `🎉 Récompense obtenue : ${resp.data.reward_description || 'palier atteint'}`,
          { duration: 6000 }
        );
      }
      fetchCustomer();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail === 'Insufficient points'
        ? `Le client n’a pas assez de ${unit} sur cette carte.`
        : (typeof detail === 'string' ? detail : `Erreur lors de la mise à jour des ${unit}`));
    }
  };

  const downloadApplePass = async (cardId) => {
    try {
      const response = await walletAPI.applePass(cardId, customer.id);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      const card = cards.find(c => c.id === cardId);
      a.download = `${card?.name || 'card'}_${customer.name}.pkpass`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err.response?.status !== 401) {
        setError('Erreur lors du téléchargement du pass Apple Wallet');
      }
    }
  };

  const getGoogleWalletLink = async (cardId) => {
    try {
      const { data } = await walletAPI.googleSaveLink(cardId, customer.id);
      if (data.save_url) {
        window.open(data.save_url, '_blank');
      } else {
        setError('Google Wallet non configuré. Veuillez configurer les identifiants.');
      }
    } catch (err) {
      if (err.response?.status !== 401) {
        setError('Erreur lors de la génération du lien Google Wallet');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard/customers')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Retour
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Ajouter un client' : customer?.name}
          </h1>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <Tabs defaultValue="details" className="w-full">
        <TabsList>
          <TabsTrigger value="details" data-testid="tab-details">Informations</TabsTrigger>
          {!isNew && <TabsTrigger value="points" data-testid="tab-points">{allStamps ? 'Tampons' : 'Points'}</TabsTrigger>}
          {!isNew && <TabsTrigger value="qrcode" data-testid="tab-qrcode">QR Code</TabsTrigger>}
          {!isNew && <TabsTrigger value="wallet" data-testid="tab-wallet">Pass Wallet</TabsTrigger>}
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Informations client</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Jean Dupont"
                      required
                      data-testid="customer-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="jean@exemple.com"
                      data-testid="customer-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+33 6 12 34 56 78"
                      data-testid="customer-phone-input"
                    />
                  </div>

                  <p className="text-sm text-slate-500">Email ou téléphone requis</p>

                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={saving} data-testid="save-customer-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    {isNew ? 'Ajouter le client' : 'Enregistrer'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {!isNew && customer && (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle>Statistiques</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    <div className="text-center px-2 py-4 bg-blue-50 rounded-lg min-w-0">
                      <p className="text-3xl font-bold text-blue-600">{customer.total_points}</p>
                      <p className="text-xs sm:text-sm leading-tight text-blue-600">{allStamps ? 'Tampons reçus' : 'Points cumulés'}</p>
                    </div>
                    <div className="text-center px-2 py-4 bg-green-50 rounded-lg min-w-0">
                      <p className="text-3xl font-bold text-green-600">{customer.total_visits}</p>
                      <p className="text-xs sm:text-sm leading-tight text-green-600">Visites</p>
                    </div>
                    <div className="text-center px-2 py-4 bg-purple-50 rounded-lg min-w-0">
                      <p className="text-3xl font-bold text-purple-600">{customer.total_redemptions}</p>
                      <p className="text-xs sm:text-sm leading-tight text-purple-600">Récompenses</p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="font-medium text-slate-900 mb-3">Soldes des cartes</h4>
                    {customer.card_balances?.length > 0 ? (
                      <div className="space-y-2">
                        {customer.card_balances.map((balance) => {
                          const card = cards.find(c => c.id === balance.card_id);
                          return (
                            <div key={balance.card_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-4 h-4 rounded"
                                  style={{ backgroundColor: card?.primary_color || '#ccc' }}
                                />
                                <span className="text-slate-700">{card?.name || 'Carte inconnue'}</span>
                              </div>
                              <span className="font-bold text-slate-900">{balanceLabel(card, balance.points)}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">Aucun solde pour le moment</p>
                    )}
                  </div>

                  <div className="mt-6">
                    <h4 className="font-medium text-slate-900 mb-3">Transactions récentes</h4>
                    {customer.recent_transactions?.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {customer.recent_transactions.map((tx) => (
                          <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div>
                              <p className="text-sm text-slate-700">{tx.description}</p>
                              <p className="text-xs text-slate-500">{new Date(tx.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</p>
                            </div>
                            <span className={`font-bold ${tx.transaction_type === 'redeem' ? 'text-red-600' : 'text-green-600'}`}>
                              {tx.transaction_type === 'redeem' ? '−' : '+'}{tx.points}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">Aucune transaction pour le moment</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {!isNew && (
          <TabsContent value="points" className="mt-6">
            <Card className="border-slate-200 max-w-xl">
              <CardHeader>
                <CardTitle>{unit === 'tampons' ? 'Gérer les tampons' : 'Gérer les points'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Carte de fidélité</Label>
                  {customer?.card_balances?.length > 0 ? (
                    <Select
                      value={pointsForm.card_id}
                      onValueChange={(value) => setPointsForm({ ...pointsForm, card_id: value })}
                    >
                      <SelectTrigger data-testid="card-select">
                        <SelectValue placeholder="Choisir une carte" />
                      </SelectTrigger>
                      <SelectContent>
                        {customer.card_balances.map((balance) => {
                          const card = cards.find(c => c.id === balance.card_id);
                          if (!card) return null;
                          return (
                            <SelectItem key={card.id} value={card.id}>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded"
                                  style={{ backgroundColor: card.primary_color }}
                                />
                                {card.name}
                                <span className="text-xs text-slate-400 ml-1">({balanceLabel(card, balance.points)})</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-sm text-slate-500" data-testid="manage-points-no-enrollment">
                      Ce client n'est inscrit à aucune carte. Demandez-lui de scanner le QR d'une carte pour s'inscrire.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Nombre de {unit}</Label>
                  <Input
                    type="number"
                    min="1"
                    value={pointsForm.points}
                    onChange={(e) => setPointsForm({ ...pointsForm, points: e.target.value.replace(/[^0-9]/g, '') })}
                    data-testid="points-amount-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    onClick={() => handleAddPoints('earn')}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="add-points-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" /> Ajouter des {unit}
                  </Button>
                  <Button
                    onClick={() => handleAddPoints('redeem')}
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    data-testid="redeem-points-btn"
                  >
                    <Minus className="w-4 h-4 mr-2" /> {unit === 'tampons' ? 'Retirer des tampons' : 'Utiliser des points'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {!isNew && customer && (
          <TabsContent value="qrcode" className="mt-6">
            <div className="max-w-md">
              {/* Same link as the Wallet pass QR: the iPhone camera opens the
                  scan screen directly, and the in-app scanner reads it too. */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5" /> Code membre
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <div className="p-6 bg-white rounded-xl border-2 border-slate-200 shadow-inner">
                    <QRCodeSVG 
                      value={customer.member_code
                        ? `${window.location.origin}/s/${customer.member_code}${customer.merchant_id ? `?mid=${customer.merchant_id}` : ''}`
                        : ''}
                      size={192}
                      level="M"
                      includeMargin={false}
                    />
                  </div>
                  <p className="mt-4 text-sm text-slate-600 text-center">
                    Scannez-le avec l’appareil photo du téléphone, comme la carte Wallet
                  </p>
                  <p className="mt-1 text-sm text-slate-500 font-mono text-center">
                    {customer.member_code}
                  </p>
                </CardContent>
              </Card>

            </div>
          </TabsContent>
        )}

        {!isNew && customer && (
          <TabsContent value="wallet" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" /> Pass Wallet pour {customer.name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customer.card_balances?.length > 0 ? (
                  <div className="space-y-4">
                    {customer.card_balances.map((balance) => {
                      const card = cards.find(c => c.id === balance.card_id);
                      if (!card) return null;
                      return (
                        <div 
                          key={card.id} 
                          className="p-4 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
                          data-testid={`wallet-card-row-${card.id}`}
                        >
                          <div className="flex items-center gap-4 mb-4">
                            <div 
                              className="w-12 h-12 rounded-lg flex items-center justify-center shadow-sm"
                              style={{ backgroundColor: card.primary_color }}
                            >
                              <CreditCard className="w-6 h-6" style={{ color: card.secondary_color }} />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-slate-900">{card.name}</h3>
                              <p className="text-sm text-slate-500">
                                {balance.points} points • {card.reward_threshold} pts pour récompense
                              </p>
                              {/* P2-2 — wallet installation badges */}
                              <div className="flex flex-wrap gap-2 mt-2">
                                {walletStatus[card.id]?.apple_installed && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-900 text-white"
                                    data-testid={`badge-apple-installed-${card.id}`}
                                    title={`${walletStatus[card.id].apple_device_count} appareil(s) Apple enregistré(s)`}
                                  >
                                    <AppleIcon className="w-3 h-3" />
                                    Apple Wallet installé
                                  </span>
                                )}
                                {walletStatus[card.id]?.google_save_link_generated && (
                                  <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800"
                                    data-testid={`badge-google-saved-${card.id}`}
                                  >
                                    <Smartphone className="w-3 h-3" />
                                    Google Wallet enregistré
                                  </span>
                                )}
                                {!walletStatus[card.id]?.apple_installed &&
                                 !walletStatus[card.id]?.google_save_link_generated && (
                                  <span
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-500"
                                    data-testid={`badge-no-wallet-${card.id}`}
                                  >
                                    Aucun wallet installé
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-3">
                            <Button
                              onClick={() => downloadApplePass(card.id)}
                              className="flex-1 bg-black hover:bg-gray-800 text-white"
                              data-testid={`apple-wallet-btn-${card.id}`}
                            >
                              <AppleIcon className="w-5 h-5 mr-2" />
                              Add to Apple Wallet
                            </Button>
                            <Button
                              onClick={() => getGoogleWalletLink(card.id)}
                              variant="outline"
                              className="flex-1"
                              data-testid={`google-wallet-btn-${card.id}`}
                            >
                              <Smartphone className="w-5 h-5 mr-2" />
                              Ajouter à Google Wallet
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8" data-testid="wallet-no-enrollment">
                    <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Ce client n'est inscrit à aucune carte.</p>
                    <p className="text-sm text-slate-400 mt-1">Demandez-lui de scanner le QR d'une de vos cartes pour s'inscrire.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default CustomerDetailPage;
